import React, {} from 'react';
import { Card, CardContent} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileText, Plus, Eye, Edit, Trash2} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RulesListView = ({ rules, token, versionId: versionId, isDraft, onUpdate, onEditRule, onNewRule }) => {
  const getSeverityBadge = (severity) => {
    const config = {
      hard: { color: 'bg-red-100 text-red-800', icon: '🛑' },
      soft: { color: 'bg-yellow-100 text-yellow-800', icon: '⚠️' },
      ess: { color: 'bg-purple-100 text-purple-800', icon: '🤝' },
      warning: { color: 'bg-blue-100 text-blue-800', icon: '💡' }
    };
    const c = config[severity] || config.warning;
    return <Badge className={c.color}>{c.icon} {severity.toUpperCase()}</Badge>;
  };

  const toggleRule = async (ruleId) => {
    try {
      await fetch(`${API_URL}/api/admin/rules/${ruleId}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onUpdate();
    } catch {
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm('Supprimer cette règle ?')) return;
    try {
      await fetch(`${API_URL}/api/admin/rules/${ruleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onUpdate();
    } catch {
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Badge variant="outline">🛑 {rules.filter(r => r.severity === 'hard').length} Hard</Badge>
          <Badge variant="outline">⚠️ {rules.filter(r => r.severity === 'soft').length} Soft</Badge>
          <Badge variant="outline">🤝 {rules.filter(r => r.severity === 'ess').length} ESS</Badge>
        </div>
        {isDraft && (
          <Button size="sm" onClick={onNewRule} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle règle
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {rules.sort((a, b) => a.priority - b.priority).map(rule => (
          <Card key={rule.id} className={`${!rule.is_active ? 'opacity-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-bold">{rule.rule_key}</code>
                    {getSeverityBadge(rule.severity)}
                    {!rule.is_active && <Badge variant="outline" className="text-gray-500">Désactivé</Badge>}
                    <span className="text-xs text-gray-400">Priorité: {rule.priority}</span>
                  </div>
                  <h4 className="font-medium text-gray-800">{rule.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{rule.description}</p>
                  
                  {/* Messages preview */}
                  {rule.messages?.fr && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-sm border-l-2 border-blue-400">
                      <span className="text-gray-500">🇫🇷</span> {rule.messages.fr.substring(0, 100)}...
                    </div>
                  )}
                </div>
                
                {isDraft && (
                  <div className="flex gap-1 ml-4">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => toggleRule(rule.id)}
                      title={rule.is_active ? 'Désactiver' : 'Activer'}
                    >
                      <Eye className={`w-4 h-4 ${rule.is_active ? 'text-green-600' : 'text-gray-300'}`} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => onEditRule(rule)}
                    >
                      <Edit className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500"
                      onClick={() => deleteRule(rule.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {rules.length === 0 && (
          <Card className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Aucune règle définie</p>
            {isDraft && (
              <Button className="mt-4" onClick={onNewRule}>
                <Plus className="w-4 h-4 mr-2" /> Créer la première règle
              </Button>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

// Test Panel
